/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **Duplicate an artifact — one gesture, every outcome** (`P3a`, ratified by Todd 2026-08-20;
 * widened by `ST12` item 2 / `#1358`, ruled by Todd 2026-09-17).
 *
 * ## One entry point, and every outcome chosen in here (`ST12` item 2)
 *
 * `DemandSourcesPage` rendered **two** buttons — *Duplicate* and *Save as template* — both opening
 * this dialog, with the template outcome set by which one was pressed and the relation set by a
 * radio inside. So three reachable outcomes were chosen two different ways, one of them before the
 * dialog opened. The comment beside the second button argued the right principle one step short:
 * *"beside Duplicate because it is the same gesture with a different outcome, and separating them
 * would be the second vocabulary `TM9` forbids"* — **two adjacent buttons *are* the separation.**
 *
 * > **Sharing the dialog is not sufficient; share the gesture.** The same shape as *sharing the
 * > widget is not sufficient, share the round trip*, and invisible for the same reason: each surface
 * > was internally consistent, so nothing could detect it.
 *
 * And since the wizard reaches it from an **open editor**, the dialog now declares what it is
 * copying rather than letting every caller's assumption ride — see {@link CloneSubject}.
 *
 * > *"For creating a variant, I think it's a choice within the 'clone' operation… The decision needs
 * > to be obvious to the user, defaults to 'extend' or 'inherit from parent' vs. 'save as
 * > independent/detached' — with appropriate simple direction that explains what each means."*
 *
 * **Not two buttons.** Cloning and extending are the same intent — *"another one like this"* —
 * differing only in whether the copy keeps a live link. Two adjacent actions producing visually
 * identical artifacts that behave differently months later is how people pick wrongly and find out
 * long afterwards, with nothing on screen to have warned them.
 *
 * ## It asks for the copy's identity — added 2026-08-24 (`A7`, `#1148`/`#1146`)
 *
 * The dialog used to ask only *how* to copy, and **derive what it should have asked for**: the
 * server minted `${slug(name)}-${Date.now()}` and appended `(Copy)` to the name. Two consequences,
 * both live in the testing store:
 *
 * - **`#1148`** — a duplicated request strategy got `demo-app-strategy-copy-1787267549846`, which is
 *   frozen, caller-facing and unreadable. Strategy ids are the half of A7 that was already correct:
 *   required, validated, referenced by callers as `requestStrategyId`. Clone was a third write path,
 *   and *a rule attached to one write path lapses the moment another is added* (CLAUDE.md).
 * - **`#1146`** — nothing checked the name, so duplicating `House` twice gave two `House (Variant)`.
 *
 * So the id is **authored here**, exactly as the wizard and the strategy editors author one:
 * suggested from the name so it costs a keystroke rather than a decision, and the suggestion stops
 * the moment the operator types in the box — an id that keeps re-deriving from the name is an id
 * that changes, which is the one thing an authored identifier must never do.
 *
 * **The name is a proposal, disambiguated rather than refused.** An operator pressing Duplicate has
 * not made a mistake, so where `House (Variant)` is taken the proposal is `House (Variant) 2` — and
 * it sits in an editable field, because the operator usually knows a better answer than the suffix.
 *
 * ## Three things this component is careful about
 *
 * - **The explanation is the control, not a tooltip.** #1045's rule: naming a mechanism is not
 *   describing an effect. Each option carries the sentence that says what it *does* — *"takes future
 *   changes to the original"* — rather than the sentence that names the machinery.
 * - **The default is the recoverable choice.** An extended copy can be detached later and keeps
 *   every value; an independent copy cannot be retro-linked without guessing which of its values
 *   were meant to track. *Default to the choice you can change your mind about.*
 * - **Shared, not demand-source shaped.** Every artifact type gets this gesture eventually
 *   (`#904` designs owner/visibility/lifecycle across all three source types), so the copy here
 *   talks about *this* and *the original* rather than about sources.
 */

import React, { useState } from 'react';
import { Box, Button, Heading, HStack, Input, Text, VStack } from '@chakra-ui/react';

import type { CloneMode, ClonedArtifactIdentity } from '../../types/demand-source';
import {
  artifactIdPurpose,
  proposeArtifactName,
  slugifyArtifactId,
  validateArtifactId,
} from '../../utils/artifact-identity';
import { type ArtifactOutcomeOption, CLONE_OPTIONS, DEFAULT_CLONE_MODE } from './clone-options';
import { copyMayExtend, templateExtendsOnlyATemplate } from '@artifact-access';
import { useBackdropDismiss } from '../ui/useBackdropDismiss';
import { TEMPLATE_DISCLOSURE_HEADLINE, type TemplateDisclosureLine } from './template-disclosure';
import { TemplateDisclosureList } from './TemplateDisclosureList';

/**
 * **What this dialog is copying** — declared by the caller, never inferred (`ST12` item 2, `#1358`).
 *
 * `onConfirm` hands back an identity and the **caller** does the cloning, so the caller is the only
 * thing that knows whether it will send the stored record or the draft on screen. Every caller was a
 * list until the wizard gained *Save as…*, so the subject had only ever been the stored artifact and
 * nobody had to say so — and the failure mode of getting it wrong from an open editor is a dialog
 * that looks right and copies the wrong thing, silently losing exactly the work that made the
 * operator reach for the gesture.
 *
 * Three answers, not two (`X33a`): `null` is *the question does not arise* — a list row with no
 * editor of this artifact open, where there is no draft that could differ. It is not *unknown*, and
 * a surface that has a draft may not use it.
 */
export type CloneSubject = 'stored' | 'unsaved';

export interface CloneArtifactDialogProps {
  /** The artifact being copied, for the heading — so the operator knows which row they clicked. */
  sourceName: string;
  /**
   * **Is the thing being copied already a template?** (`ST12` item 3, ruled 2026-09-22.)
   *
   * The naming rule is **directional** — the word *Template* comes off going out of one and goes on
   * going in — so the proposal cannot be made from the target alone. It is also what makes the
   * **target** knowable on a surface with no choice: `isTemplate` rides through a clone unchanged
   * (`ClonedArtifactIdentity.asTemplate`), so copying a template produces a template whether or not
   * anybody was asked.
   */
  sourceIsTemplate: boolean;
  /**
   * **What one of these is called, lower case** (`demand source`, `request strategy`): the template
   * sentence says what can be started from a template. Required, because the dialog stands over every
   * type, and it said *demand source* on a strategy's copy until slice 7b.
   */
  instanceNoun: string;
  /**
   * **What will be copied**, stated on screen. See {@link CloneSubject}. Required and nullable so
   * the compiler asks every caller (`#1134`) rather than letting the next editor entry point inherit
   * a list's assumption.
   */
  subject: CloneSubject | null;
  /**
   * Names already in use, so the **proposal** does not collide with one the operator can see.
   *
   * Deliberately a suggestion input and not a validator. A client holds a visibility-filtered — and
   * on this page paginated — slice of the store, so a console-side uniqueness *verdict* would be
   * confidently wrong about exactly the artifacts it cannot see. **Both** id and name are judged
   * server-side against the whole store, where they can be judged correctly; passing names here only
   * spares the operator the common bounce. Passing none is therefore always safe.
   */
  existingNames?: readonly string[];
  /**
   * Does this artifact type offer the extend/independent choice?
   *
   * **Absent means no**, and the dialog then asks only for the copy's identity. Offering *Extend*
   * where a type has no chips, no revert and no *"Used by"* would be a runtime capability with no
   * authoring surface — P-rule 1, the exact invoice `#400` left on the board. Demand sources have
   * that surface (`P3a`); strategies and rule sets do not yet, so their copies are independent and
   * the dialog says nothing about a choice they cannot make.
   */
  offersInheritance?: boolean;
  /**
   * **Is this copy a template, and which does it start on** (`TM-C`, `#1171`; `ST12` item 2).
   *
   * The **same** dialog rather than a second one, because `TM9` is that `isTemplate` is orthogonal
   * to the relation the new artifact takes: an operator saving a template still chooses extend or
   * independent, and offering that choice through a parallel widget is where the two vocabularies
   * start to differ.
   *
   * **It moved inside the dialog here.** It used to be a caller-set boolean, chosen by *which of two
   * adjacent buttons the operator pressed* — so the three reachable outcomes were picked two
   * different ways, one before the dialog opened and two inside it. Sharing the dialog is not
   * sufficient; share the **gesture**.
   *
   * `null` is a type with no template plane at all (strategies, rule sets) — *the question does not
   * arise* — and is also correct for copying a template, where `isTemplate` rides through unchanged.
   * Required, so a type that gains one cannot forget to offer it (`#1134`).
   *
   * **The prohibition is still load-bearing: one gesture must never silently do the other.** The
   * heading, the disclosure and the confirm button all follow the choice rather than the caller.
   */
  templateOutcome: { initial: boolean; options: readonly ArtifactOutcomeOption[] } | null;
  /**
   * **What the copy would carry as a template**, shown before it is saved (`TM5`).
   *
   * Passed rather than derived here, because the dialog is type-agnostic and *what a document holds*
   * is not. `describeTemplateContents` is the demand-source answer; the next type brings its own.
   *
   * Passed **whatever the caller thinks the outcome is**, now that the outcome is chosen in here:
   * a caller that only computed it for a template could not answer once the operator changed
   * their mind mid-dialog.
   */
  disclosure?: readonly TemplateDisclosureLine[];
  isPending?: boolean;
  error?: string | null;
  onCancel: () => void;
  onConfirm: (copy: ClonedArtifactIdentity) => void;
}

/**
 * The word that names the relationship, not the act — see {@link proposeCopyName}.
 *
 * **It stopped depending on the template choice** (`ST12` item 3, `#1359`; Todd 2026-09-17). It
 * returned `Template` for a template, on the reasoning that *a template's proposed name says what it
 * is* — which reverses here, and the two positions reconcile rather than collide: the docblock
 * wanted a name that says what the thing is, Todd wants a default that can be accepted. A suggestion
 * that is unique and does not editorialise satisfies both, where a suffix that has to be deleted
 * before saving satisfies neither. **A default that cannot be accepted is not a default** — it is a
 * prompt with extra steps.
 *
 * It is also what makes the outcome safe to change inside the dialog: a name that re-derives from
 * *what you are making* would rewrite the box under an operator who had already accepted it.
 */
function suffixFor(mode: CloneMode, offersInheritance: boolean): string {
  return offersInheritance && mode === 'extend' ? 'Variant' : 'Copy';
}

export const CloneArtifactDialog: React.FC<CloneArtifactDialogProps> = ({
  sourceName,
  sourceIsTemplate,
  instanceNoun,
  subject,
  existingNames = [],
  offersInheritance = false,
  templateOutcome,
  disclosure,
  isPending,
  error,
  onCancel,
  onConfirm,
}) => {
  /**
   * **Whether *extend* is on offer for a copy that will or will not be a template** (`#1599`): a
   * template's parent is nothing or another template, so a template from a live artifact starts on its
   * own. The server refuses the same combination with the same sentence.
   */
  const extendOfferedFor = (copyIsTemplate: boolean): boolean =>
    offersInheritance && copyMayExtend({ isTemplate: sourceIsTemplate }, copyIsTemplate);
  const initialMode: CloneMode = extendOfferedFor(templateOutcome ? templateOutcome.initial === true : sourceIsTemplate)
    ? DEFAULT_CLONE_MODE
    : 'independent';

  const [mode, setMode] = useState<CloneMode>(initialMode);
  /**
   * What the copy **is**. `false` where the type has no template plane, so the value the identity
   * carries is the same shape either way and no reader has to know which control produced it.
   */
  const [asTemplate, setAsTemplate] = useState(templateOutcome?.initial === true);
  /**
   * **What the copy will be**, which is not always what was chosen: `isTemplate` rides through a
   * clone, so a copy of a template is one whether or not this surface offered the question.
   */
  //
  // **Where the surface offers the choice, the choice decides** (slice 7b): *create a strategy from this
  // template* is an instance made from a template, which only a stated `false` produces.
  const targetIsTemplate = templateOutcome ? asTemplate : sourceIsTemplate;
  const extendOffered = extendOfferedFor(targetIsTemplate);
  /** What is sent: the chosen relation, or *independent* where extend is not on offer for this outcome. */
  const effectiveMode: CloneMode = extendOffered ? mode : 'independent';

  /** The proposal, for the mode and outcome currently selected. See {@link proposeArtifactName}. */
  const nameFor = (forMode: CloneMode, forTemplate: boolean): string =>
    proposeArtifactName({
      sourceName,
      sourceIsTemplate,
      targetIsTemplate: templateOutcome ? forTemplate : sourceIsTemplate,
      relation: suffixFor(forMode, offersInheritance),
      taken: existingNames,
    });

  const initialName = nameFor(initialMode, templateOutcome?.initial === true);
  const [name, setName] = useState(initialName);
  const [id, setId] = useState(slugifyArtifactId(initialName));
  /**
   * Once the operator has typed in a box, that box stops following the one above it.
   *
   * Two levels of the same rule: the name follows the mode until they name it, and the id follows
   * the name until they choose one. Local state rather than derived, because *"has a person spoken
   * about this"* is not recoverable from the values — `house-variant` is both the suggestion and a
   * perfectly reasonable thing to have typed.
   */
  const [nameTouched, setNameTouched] = useState(false);
  const [idTouched, setIdTouched] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  /** Renaming re-suggests an id; changing the mode re-suggests both. Each stops where it was told to. */
  const applyName = (next: string, touched: boolean) => {
    setName(next);
    if (touched) setNameTouched(true);
    if (!idTouched) setId(slugifyArtifactId(next));
  };

  const changeMode = (next: CloneMode) => {
    setMode(next);
    if (!nameTouched) applyName(nameFor(next, asTemplate), false);
  };

  /**
   * **Changing the outcome re-proposes the name**, because the rule depends on it: the same source
   * suggests `X (Variant)` for a copy and `X Template` for a template. It stops the moment the
   * operator has typed, which is the same two-level rule the id follows under the name.
   */
  const changeOutcome = (next: boolean) => {
    setAsTemplate(next);
    const nextMode = extendOfferedFor(next) ? mode : 'independent';
    if (!nameTouched) applyName(nameFor(nextMode, next), false);
  };

  const trimmedName = name.trim();
  const nameError = trimmedName ? undefined : 'Name is required.';
  const idError = validateArtifactId(id);
  // Shown once the operator has been in the box, or once they have tried to submit — a red message
  // beside a field nobody has touched reads as a failure rather than as a rule.
  const showNameError = nameError && (nameTouched || submitted);
  const showIdError = idError && (idTouched || submitted);

  const submit = () => {
    setSubmitted(true);
    if (nameError || idError) return;
    // `mode` only where the surface offers the choice — see `ClonedArtifactIdentity.mode`.
    onConfirm({
      ...(offersInheritance ? { mode: effectiveMode } : {}),
      // Stated whenever the surface offered the choice, `false` included (slice 7b); never where it did
      // not, where what the original is rides through.
      ...(templateOutcome ? { asTemplate } : {}),
      id: id.trim(),
      name: trimmedName,
    });
  };

  return (
    <Box
      role="dialog"
      aria-label={`Duplicate ${sourceName}`}
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="md"
      bg="bg.card"
      p={4}
    >
      <Heading size="sm" color="fg.title" mb={1}>
        Duplicate “{sourceName}”
      </Heading>
      {/* **What is being copied** (`ST12` item 2, `#1358`). Stated, never inferred — see
          {@link CloneSubject}. `null` says nothing, because on a list there is no draft that could
          differ and a sentence about it would be noise the operator learns to skip. */}
      {subject !== null && (
        <Text fontSize="xs" color={subject === 'unsaved' ? 'accent.ink' : 'fg.muted'} mb={2}>
          {subject === 'unsaved'
            ? 'Copies what is on screen, including the changes you have not saved.'
            : 'Copies this as it was last saved. Unsaved changes elsewhere are not included.'}
        </Text>
      )}
      {targetIsTemplate && (
        <Text fontSize="xs" color="fg.muted" mb={3}>
          The original is unchanged. The template appears under Templates, where anyone who can see it can start a new{' '}
          {instanceNoun} from it.
        </Text>
      )}
      {extendOffered && (
        <Text fontSize="xs" color="fg.muted" mb={3}>
          You can change this later: an extended copy can be detached and keeps every value.
        </Text>
      )}

      {/* **What are you making?** — the choice that used to be *which of two buttons you pressed*
          (`ST12` item 2, `#1358`). Above the relation because it is the bigger of the two: a template
          never serves a request, where extend and independent both do. */}
      {templateOutcome && (
        <Box mb={4}>
          <Text fontSize="xs" fontWeight="600" color="fg.default" mb={1.5}>
            What are you making?
          </Text>
          <HStack gap={2} wrap="wrap" align="stretch">
            {templateOutcome.options.map((option) => {
              const selected = asTemplate === option.asTemplate;
              return (
                <Box
                  key={option.label}
                  as="button"
                  textAlign="left"
                  flex="1 1 12rem"
                  borderWidth="1px"
                  borderRadius="md"
                  borderColor={selected ? 'accent.solid' : 'border.default'}
                  bg={selected ? 'accent.tint' : 'transparent'}
                  _hover={{ borderColor: 'accent.solid' }}
                  p={2.5}
                  aria-pressed={selected}
                  onClick={() => changeOutcome(option.asTemplate)}
                >
                  <Text fontSize="sm" fontWeight="600" color={selected ? 'accent.ink' : 'fg.default'}>
                    {option.label}
                  </Text>
                  <Text fontSize="xs" color="fg.muted" mt={0.5}>
                    {option.detail}
                  </Text>
                </Box>
              );
            })}
          </HStack>
        </Box>
      )}

      {/* **What travels, before it does** (`TM5`). Above the name and the relation deliberately: it
          is the decision the operator may need to go back and change something for, and a panel
          under the buttons is one they will read after they have pressed one. */}
      {targetIsTemplate && disclosure && disclosure.length > 0 && (
        <Box borderWidth="1px" borderColor="border.default" borderRadius="md" bg="bg.canvas" p={3} mb={4}>
          <Text fontSize="xs" color="fg.default" mb={2}>
            {TEMPLATE_DISCLOSURE_HEADLINE}
          </Text>
          {/* `null`: this block only renders when there are lines, so the empty case never arrives
              here — the wizard's phase is the surface that meets it. */}
          <TemplateDisclosureList lines={disclosure} emptyNote={null} />
        </Box>
      )}

      {offersInheritance && (
        <VStack align="stretch" gap={2} mb={4}>
          {offersInheritance && !extendOffered && (
            <Text fontSize="xs" color="fg.muted">
              {templateExtendsOnlyATemplate(instanceNoun)}
            </Text>
          )}
          {CLONE_OPTIONS.filter((option) => extendOffered || option.mode === 'independent').map((option) => {
            const selected = effectiveMode === option.mode;
            return (
              <Box
                key={option.mode}
                as="button"
                textAlign="left"
                borderWidth="1px"
                borderRadius="md"
                borderColor={selected ? 'accent.solid' : 'border.default'}
                bg={selected ? 'accent.tint' : 'transparent'}
                _hover={{ borderColor: 'accent.solid' }}
                p={3}
                onClick={() => changeMode(option.mode)}
                aria-pressed={selected}
              >
                <Text fontSize="sm" fontWeight="600" color={selected ? 'accent.ink' : 'fg.default'}>
                  {option.label}
                  {option.recommended && (
                    <Text as="span" fontSize="xs" fontWeight="400" color="fg.muted" ml={2}>
                      recommended
                    </Text>
                  )}
                </Text>
                <Text fontSize="xs" color="fg.muted" mt={0.5}>
                  {option.detail}
                </Text>
              </Box>
            );
          })}
        </VStack>
      )}

      {/* **What the copy is called, and what it is referenced by** (A7, `#1148`). Both proposed so
          duplicating stays one gesture, both editable because the proposal is a guess about a name
          only the operator knows. */}
      <VStack align="stretch" gap={3}>
        <Box>
          <Text fontSize="xs" fontWeight="600" color="fg.default" mb={1}>
            Name
          </Text>
          <Input
            aria-label="Name"
            value={name}
            onChange={(e) => applyName(e.target.value, true)}
            size="sm"
            borderColor={showNameError ? 'error.border' : 'border.default'}
            _focus={{ borderColor: 'accent.solid', boxShadow: '0 0 0 1px var(--chakra-colors-accent-solid)' }}
          />
          {showNameError && (
            <Text fontSize="xs" color="error.ink" mt={1}>
              {nameError}
            </Text>
          )}
        </Box>

        <Box>
          <Text fontSize="xs" fontWeight="600" color="fg.default" mb={1}>
            ID
          </Text>
          <Input
            aria-label="ID"
            value={id}
            onChange={(e) => {
              setId(e.target.value);
              setIdTouched(true);
            }}
            size="sm"
            fontFamily="mono"
            borderColor={showIdError ? 'error.border' : 'border.default'}
            _focus={{ borderColor: 'accent.solid', boxShadow: '0 0 0 1px var(--chakra-colors-accent-solid)' }}
          />
          <Text fontSize="xs" color="fg.muted" mt={1}>
            {showIdError ? (
              <Text as="span" color="error.ink">
                {idError}
              </Text>
            ) : (
              `${artifactIdPurpose(targetIsTemplate ? 'template' : 'artifact')} Cannot be changed afterwards.`
            )}
          </Text>
        </Box>
      </VStack>

      {error && (
        <Text fontSize="xs" color="error.ink" mt={3}>
          {error}
        </Text>
      )}

      <HStack justify="flex-end" gap={2} mt={4}>
        <Button size="sm" variant="ghost" color="fg.muted" onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
        <Button
          size="sm"
          bg="accent.solid"
          color="white"
          _hover={{ bg: 'accent.ink' }}
          onClick={submit}
          disabled={isPending}
        >
          {/* The button names the act the **choice** produces, not the one the caller opened
              (`TM9`: one gesture must never silently do the other). */}
          {isPending ? (asTemplate ? 'Saving…' : 'Duplicating…') : asTemplate ? 'Save as template' : 'Duplicate'}
        </Button>
      </HStack>
    </Box>
  );
};

/**
 * The dialog in its modal backdrop.
 *
 * Four pages now open this gesture — demand sources, both strategy types and rule sets — and the
 * backdrop was hand-rolled on the one that had it. A second copy is where the two start to differ
 * (the click-outside guard while a write is in flight is the easy one to forget), so it ships with
 * the dialog rather than beside it.
 */
export const CloneArtifactOverlay: React.FC<CloneArtifactDialogProps> = (props) => {
  // Not while a write is in flight: dismissing the dialog would leave the operator with no way to
  // read the outcome of a duplicate that is already happening.
  //
  // And a **drag out of the name field is not a click on the backdrop** — see `useBackdropDismiss`.
  // This is the dialog it was reported against: selecting the suggested name and dragging past the
  // dialog's edge released on the backdrop, and the clone was cancelled with the typing in it.
  const backdrop = useBackdropDismiss(props.onCancel, { disabled: props.isPending });

  return (
    <Box
      position="fixed"
      inset={0}
      bg="blackAlpha.600"
      display="flex"
      alignItems="center"
      justifyContent="center"
      zIndex={1400}
      p={4}
      {...backdrop}
    >
      {/* No `stopPropagation` here any more: the hook decides on the event's own target, so a click
          inside never reads as a backdrop click — and swallowing clicks on a container is the kind
          of guard that quietly breaks the next control added inside it. */}
      <Box maxW="480px" w="100%">
        <CloneArtifactDialog {...props} />
      </Box>
    </Box>
  );
};
